// ============================================================================
// Puente a ingeniería (#510) — tipos y reglas PURAS del panel.
// ============================================================================
// Al APROBAR un ticket para el agente de código, el backend encola el trabajo
// del runner y crea (o reutiliza) el issue de GitHub. Si quedó «pendiente»
// (sin token, sin repo resuelto) o «falló», el staff lo reintenta desde el
// panel del agente IA. Puro (sin api/firebase) para poder probarlo aislado.
// ============================================================================

export type EngineeringBridgeState = "pending" | "creating" | "linked" | "failed";

export interface EngineeringBridgeInfo {
  state: EngineeringBridgeState;
  repo?: string | null;
  /** github_not_configured | repo_not_resolved | github_error | create_uncertain | verify_failed | repo_not_allowed | … */
  reason?: string | null;
  message?: string | null;
  labelled?: boolean;
  attempts?: number;
  updatedAt?: number | null;
}

/** Colores del chip; el texto sale de i18n (ticketDetail.bridge.states.<estado>). */
export const ENGINEERING_BRIDGE_COLORS: Record<EngineeringBridgeState, { color: string; bg: string }> = {
  pending: { color: "#b45309", bg: "#fef3c7" },
  creating: { color: "#2563eb", bg: "#dbeafe" },
  linked: { color: "#059669", bg: "#d1fae5" },
  failed: { color: "#dc2626", bg: "#fee2e2" },
};

export const ENGINEERING_BRIDGE_STATES = Object.keys(ENGINEERING_BRIDGE_COLORS) as EngineeringBridgeState[];

/** Estado conocido o `pending` (un valor inesperado no rompe el chip). */
export function bridgeStateOf(bridge: EngineeringBridgeInfo | null | undefined): EngineeringBridgeState {
  const state = bridge?.state;
  return state && ENGINEERING_BRIDGE_STATES.includes(state) ? state : "pending";
}

/** ¿Mostrar «Reintentar puente»? Sólo cuando no hay issue y nada está en curso. */
export function canRetryEngineeringBridge(bridge: EngineeringBridgeInfo | null | undefined): boolean {
  return bridge?.state === "pending" || bridge?.state === "failed";
}

/** ¿El puente necesita que el staff elija el repositorio? */
export function engineeringBridgeNeedsRepo(bridge: EngineeringBridgeInfo | null | undefined): boolean {
  return bridge?.reason === "repo_not_resolved";
}

/**
 * Repos que el selector del puente puede ofrecer: la lista blanca de la mesa
 * (`settings.githubRepos`), deduplicada sin distinguir mayúsculas — la misma
 * comparación que hace el backend. NO la lista de repos que alcanza el token
 * (`GET /serviceDesk/github/repositories`): el backend rechaza con 422 todo
 * repo fuera de la lista blanca, así que ofrecerlos sólo producía errores. Una
 * lista vacía significa que el staff no puede resolverlo desde aquí: falta
 * GH_DEFAULT_REPO o configurar los repos de la mesa.
 */
export function bridgeRepoChoices(githubRepos: readonly unknown[] | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of githubRepos ?? []) {
    const value = typeof raw === "string" ? raw.trim() : "";
    if (!/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/.test(value)) continue;
    if (!out.some((r) => r.toLowerCase() === value.toLowerCase())) out.push(value);
  }
  return out;
}
